/**
 * CashbackBR — Integrity Guard (Anti-Tampering Runtime Shield)
 *
 * Detects code injection, DOM manipulation, unauthorized cloning,
 * and right-click/devtools abuse in production.
 *
 * Hardening (v2):
 *  - Origin check uses EXACT, normalized comparison (no startsWith prefix bypass).
 *  - Allowlist is configurable via `VITE_ALLOWED_ORIGINS` (CSV) with a safe fallback.
 *  - Violations are reported to the backend via `report-integrity-violation`
 *    edge function (sendBeacon + LGPD-minimized payload, no PII).
 *  - Severity engine (soft / warn / hard) governed by `VITE_INTEGRITY_GUARD_MODE`.
 *    Default mode is `report-only` — no destructive actions until explicitly
 *    flipped to `enforce`.
 */

import { BUILD_VERSION } from "./buildInfo";

// ---------------------------------------------------------------------------
// Allowlist (Item 1 + Item 2)
// ---------------------------------------------------------------------------

/** Canonical production origins — used as fallback when env is missing/invalid. */
const FALLBACK_ORIGINS: readonly string[] = [
  "https://cashbackbr.com.br",
  "https://www.cashbackbr.com.br",
  "https://cashbackbradesco.com.br",
  "https://www.cashbackbradesco.com.br",
  "https://cashbacknotafiscalpaulista.com.br",
  "https://www.cashbacknotafiscalpaulista.com.br",
  "https://cashbackpaulista.com.br",
  "https://www.cashbackpaulista.com.br",
  "https://cashback-buddy-br.lovable.app",
  "http://localhost:5173",
  "http://localhost:8080",
];

/** Lovable preview/sandbox domains — invariant of the platform, never env-driven. */
const LOVABLE_PREVIEW_RE =
  /^https:\/\/(?:id-preview--)?[a-z0-9-]+\.lovable(?:\.app|project\.com)$/;

function normalize(origin: string): string {
  return origin.trim().toLowerCase().replace(/\/+$/, "");
}

function parseEnvAllowlist(): string[] | null {
  try {
    const raw = import.meta.env?.VITE_ALLOWED_ORIGINS;
    if (!raw || typeof raw !== "string") return null;
    const parts = raw
      .split(",")
      .map(normalize)
      .filter((s) => /^https?:\/\/[^*\s]+$/i.test(s));
    return parts.length > 0 ? parts : null;
  } catch {
    return null;
  }
}

/** Resolved once at module load — exported for tests. */
export function getAllowedOrigins(): readonly string[] {
  const fromEnv = parseEnvAllowlist();
  if (!fromEnv) {
    // Intentional: never expand to "*". Stays a known safe list.
    return FALLBACK_ORIGINS.map(normalize);
  }
  return fromEnv;
}

const ALLOWED_ORIGINS = getAllowedOrigins();
const USING_FALLBACK = parseEnvAllowlist() === null;

// ---------------------------------------------------------------------------
// Mode + severity (Item 4)
// ---------------------------------------------------------------------------

type GuardMode = "off" | "report-only" | "enforce";

function resolveMode(): GuardMode {
  const m = import.meta.env?.VITE_INTEGRITY_GUARD_MODE;
  if (m === "off" || m === "report-only" || m === "enforce") return m;
  return "report-only";
}

const GUARD_MODE: GuardMode = resolveMode();

const IS_PRODUCTION =
  typeof window !== "undefined" &&
  !window.location.origin.includes("localhost") &&
  !window.location.origin.includes("preview--") &&
  !window.location.origin.includes("lovableproject.com");

// Known-good script/iframe injectors that should NOT trip the alarm.
const TRUSTED_INJECTORS: readonly string[] = [
  "challenges.cloudflare.com",
  "chrome-extension://",
  "moz-extension://",
  "safari-web-extension://",
];

// ---------------------------------------------------------------------------
// Violation reporter (Item 3) — LGPD-minimized, non-blocking
// ---------------------------------------------------------------------------

type ViolationType =
  | "CLONE_DETECTED"
  | "SCRIPT_INJECTION"
  | "IFRAME_INJECTION"
  | "SUPABASE_TAMPERED"
  | "KILL_SWITCH_TRIGGERED";

interface ViolationCounters {
  total: number;
  byType: Partial<Record<ViolationType, number>>;
  lastSentAt: Partial<Record<ViolationType, number>>;
}

const counters: ViolationCounters = {
  total: 0,
  byType: {},
  lastSentAt: {},
};

const MIN_INTERVAL_MS = 60_000; // 1 msg / type / minute → no flood
const MAX_VIOLATIONS = 3;

async function sha256Hex(input: string): Promise<string> {
  try {
    const buf = new TextEncoder().encode(input);
    const digest = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return "unavailable";
  }
}

async function buildReportPayload(
  type: ViolationType,
  detail: string,
): Promise<Record<string, unknown>> {
  let origin = "";
  let pathOnly = "";
  try {
    origin = normalize(window.location.origin);
    pathOnly = window.location.pathname; // strip ?query and #hash → no PII leak
  } catch {
    /* SSR */
  }
  const expectedHash = await sha256Hex(ALLOWED_ORIGINS.join("|"));
  const ua = (navigator?.userAgent ?? "").slice(0, 256);
  return {
    type,
    origin,
    path: pathOnly,
    expected_origin_hash: expectedHash,
    user_agent: ua,
    build_version: BUILD_VERSION,
    mode: GUARD_MODE,
    using_fallback_allowlist: USING_FALLBACK,
    detail: detail.slice(0, 512),
    occurred_at: new Date().toISOString(),
  };
}

function sendViolationBeacon(payload: Record<string, unknown>): void {
  try {
    // Construct URL & anon key from public Vite env so we don't depend on the
    // (possibly tampered) Supabase JS client.
    const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
    const anon =
      (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined) ||
      (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined);
    if (!url || !anon) return;
    const endpoint = `${url.replace(/\/+$/, "")}/functions/v1/report-integrity-violation`;
    const body = JSON.stringify(payload);
    // sendBeacon ignores custom headers, so we use fetch keepalive to send
    // the anon key. Both are best-effort, non-blocking.
    void fetch(endpoint, {
      method: "POST",
      keepalive: true,
      headers: {
        "content-type": "application/json",
        apikey: anon,
        authorization: `Bearer ${anon}`,
      },
      body,
    }).catch(() => {
      try {
        navigator.sendBeacon?.(endpoint, body);
      } catch {
        /* swallow */
      }
    });
  } catch {
    /* never throw from the guard */
  }
}

function reportViolation(type: ViolationType, detail: string): void {
  counters.total++;
  counters.byType[type] = (counters.byType[type] ?? 0) + 1;
  console.error(`[INTEGRITY GUARD] ${type}: ${detail}`);

  // Client-side rate limit per type
  const now = Date.now();
  const last = counters.lastSentAt[type] ?? 0;
  if (now - last >= MIN_INTERVAL_MS) {
    counters.lastSentAt[type] = now;
    void buildReportPayload(type, detail).then(sendViolationBeacon);
  }

  // Severity escalation — report-only is the SAFE default, no destructive op.
  if (GUARD_MODE !== "enforce") return;

  if (counters.total >= MAX_VIOLATIONS || type === "SUPABASE_TAMPERED") {
    // Reserved for future hard kill-switch (signOut + DOM wipeout).
    // Intentionally NOT implemented in this rollout — see plan Item 4.
    // We still emit a final beacon so backend knows escalation fired.
    void buildReportPayload(
      "KILL_SWITCH_TRIGGERED",
      `triggered_by=${type}`,
    ).then(sendViolationBeacon);
  }
}

// ---------------------------------------------------------------------------
// Origin verification (Item 1)
// ---------------------------------------------------------------------------

function isOriginAllowed(origin: string): boolean {
  const norm = normalize(origin);
  if (ALLOWED_ORIGINS.includes(norm)) return true;
  // Defense-in-depth: also validate via URL parser before regex match.
  try {
    const host = new URL(norm).hostname;
    if (!host) return false;
    if (LOVABLE_PREVIEW_RE.test(norm)) return true;
  } catch {
    return false;
  }
  return false;
}

function checkOrigin(): boolean {
  try {
    const origin = window.location.origin;
    if (!isOriginAllowed(origin)) {
      reportViolation("CLONE_DETECTED", `Unauthorized origin: ${origin}`);
      return false;
    }
  } catch {
    // SSR / test environment — skip silently
  }
  return true;
}

// ---------------------------------------------------------------------------
// DOM watchers (unchanged behavior, now uses trusted-injector whitelist)
// ---------------------------------------------------------------------------

function isTrustedSource(src: string): boolean {
  if (!src) return true;
  if (src.startsWith(window.location.origin)) return true;
  return TRUSTED_INJECTORS.some((t) => src.includes(t));
}

function watchScriptInjection() {
  if (typeof MutationObserver === "undefined") return;
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of Array.from(mutation.addedNodes)) {
        if (node instanceof HTMLScriptElement) {
          const src = node.src || "";
          if (src && !isTrustedSource(src)) {
            reportViolation("SCRIPT_INJECTION", `Foreign script: ${src}`);
            if (GUARD_MODE === "enforce") node.remove();
          }
        }
        if (node instanceof HTMLIFrameElement) {
          const src = node.src || "";
          if (src && !isTrustedSource(src)) {
            reportViolation("IFRAME_INJECTION", `Unauthorized iframe: ${src}`);
            if (GUARD_MODE === "enforce") node.remove();
          }
        }
      }
    }
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

function checkSupabaseIntegrity() {
  const expectedRef = "afqiaeahhrkpebdqdnwr";
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "";
  if (supabaseUrl && !supabaseUrl.includes(expectedRef)) {
    reportViolation("SUPABASE_TAMPERED", "Supabase URL has been modified");
  }
}

// ---------------------------------------------------------------------------
// UX hardening (unchanged)
// ---------------------------------------------------------------------------

function disableContextMenu() {
  if (!IS_PRODUCTION) return;
  document.addEventListener("contextmenu", (e) => {
    e.preventDefault();
    return false;
  });
  document.addEventListener("keydown", (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "u") {
      e.preventDefault();
      return false;
    }
    if ((e.ctrlKey || e.metaKey) && e.key === "s") {
      e.preventDefault();
      return false;
    }
    if (
      (e.ctrlKey || e.metaKey) &&
      e.shiftKey &&
      ["i", "j", "c"].includes(e.key.toLowerCase())
    ) {
      e.preventDefault();
      return false;
    }
    if (e.key === "F12") {
      e.preventDefault();
      return false;
    }
  });
}

function disableTextSelection() {
  if (!IS_PRODUCTION) return;
  const style = document.createElement("style");
  style.textContent = `
    body {
      -webkit-user-select: none !important;
      -moz-user-select: none !important;
      -ms-user-select: none !important;
      user-select: none !important;
    }
    input, textarea, [contenteditable="true"] {
      -webkit-user-select: text !important;
      -moz-user-select: text !important;
      -ms-user-select: text !important;
      user-select: text !important;
    }
  `;
  document.head.appendChild(style);
}

function watermarkConsole() {
  if (!IS_PRODUCTION) return;
  const watermark = `
%c⚠️ CashbackBR — AMBIENTE PROTEGIDO ⚠️
%cEste console é monitorado. Qualquer tentativa de engenharia reversa,
injeção de código ou acesso não autorizado será registrada e poderá
resultar em ação legal conforme Lei Carolina Dieckmann (Lei 12.737/2012)
e Marco Civil da Internet (Lei 12.965/2014).

CNPJ: 08.739.417/0001-82 — J.H.P. GALHANO JUNIOR AI LTDA
`;
  console.log(
    watermark,
    "color: #CC092F; font-size: 16px; font-weight: bold;",
    "color: #666; font-size: 12px;",
  );
}

// ---------------------------------------------------------------------------
// Public entrypoint
// ---------------------------------------------------------------------------

export function initIntegrityGuard() {
  if (typeof window === "undefined") return;
  if (GUARD_MODE === "off") return;

  checkOrigin();
  checkSupabaseIntegrity();
  watchScriptInjection();
  disableContextMenu();
  disableTextSelection();
  watermarkConsole();

  setInterval(() => {
    checkOrigin();
  }, 30_000);
}

// Test helpers — not used in production code paths.
export const __test__ = {
  isOriginAllowed,
  normalize,
  getAllowedOrigins,
  resolveMode,
  FALLBACK_ORIGINS,
};
